import { Component, OnInit, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ObjektStrukturService, RoomIndikator } from '../../../services/objekt-struktur.service';
import { IndikatorenService } from '../../../indikatoren/services/indikatoren.service';
import { Indikator } from '../../../indikatoren/interfaces/indikatoren.interface';
import { Observable } from 'rxjs';

export interface EditIndicatorData {
  id: string;
  roomId: string;
  standortId: string;
  currentData: RoomIndikator;
  // Pre-loaded settings data
  indikatorOptions: any[];
}

@Component({
  selector: 'app-edit-indicator-dialog',
  templateUrl: './edit-indicator-dialog.component.html',
  styleUrls: ['./edit-indicator-dialog.component.css'],
})
export class EditIndicatorDialogComponent implements OnInit {
  frmIndicator: UntypedFormGroup;
  indicatorId: string;
  roomId: string;
  standortId: string;
  
  // Settings dropdowns
  indikatorOptions: any[] = [];
  indikatoren: Indikator[] = [];

  loading = false;
  
  // Date fields (read-only display)
  activatedAt: string | null = null;
  deactivatedAt: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<EditIndicatorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditIndicatorData,
    private _fb: UntypedFormBuilder,
    private objektStrukturService: ObjektStrukturService,
    private indikatorenService: IndikatorenService
  ) {
    // Remove 'ind_' prefix from the ID for the API call
    const rawId = (data as any)?.id;
    this.indicatorId = typeof rawId === 'string'
      ? (rawId.startsWith('ind_') ? rawId.substring(4) : rawId)
      : '';
    this.standortId = (data as any)?.standortId || '';

    // Get settings data from dialog data
    this.indikatorOptions = (data as any)?.indikatorOptions || [];

    console.log('Edit dialog data:', data);
    console.log('Current data:', data.currentData);
    console.log('Original ID:', data.id);
    console.log('Processed indicatorId:', this.indicatorId);

    if (!this.indicatorId) {
      console.warn('EditIndicatorDialogComponent: Missing indicator id in dialog data:', data);
    }
  }

  async ngOnInit() {
    await this.createForm();
    this.loadIndikatoren();
  }

  async createForm() {
    // Handle the data structure - it might be nested under 'data' property
    let currentData = this.data.currentData;
    if (currentData && (currentData as any).data) {
      currentData = (currentData as any).data;
    }
    
    // Get roomId from currentData if not provided in data
    const roomId = this.data.roomId || (currentData as any)?.room_id;
    
    // Convert status to number (database stores as tinyint(1))
    const statusValue = (currentData as any)?.status;
    const status = statusValue !== undefined ? Number(statusValue) : 1;
    
    // Handle null/undefined values for required fields
    const schwellenwert = (currentData as any)?.schwellenwert;
    let schwellenwertValue = '';
    
    if (schwellenwert !== null && schwellenwert !== undefined && schwellenwert !== '') {
      // Convert to integer (remove decimal places)
      const numValue = parseFloat(schwellenwert);
      if (!isNaN(numValue)) {
        schwellenwertValue = Math.round(numValue).toString();
      } else {
        schwellenwertValue = schwellenwert.toString();
      }
    }
    
    // Handle indikator_number - format existing number or leave empty for auto-generation
    const existingNumber = (currentData as any)?.indikator_number;
    const indikatorNumber = existingNumber ? this.formatIndikatorNumber(existingNumber) : '';

    this.frmIndicator = this._fb.group({
      room_id: [roomId],
      standort_id: [this.standortId],
      indikator_id: [(currentData as any)?.indikator_id || '', [Validators.required]],
      name: [(currentData as any)?.name || '', [Validators.required]],
      indikator_number: [indikatorNumber], // Editable field - can be manually changed
      einheit: [(currentData as any)?.einheit || '', [Validators.required]],
      schwellenwert: [schwellenwertValue, [Validators.required]],
      bemerkung: [(currentData as any)?.bemerkung || ''],
      status: [status, [Validators.required]]
    });

    // Extract date fields for display (read-only)
    this.activatedAt = (currentData as any)?.activated_at || null;
    this.deactivatedAt = (currentData as any)?.deactivated_at || null;

    console.log('Form created with values:', this.frmIndicator.value);
    console.log('Current data extracted:', currentData);
    console.log('Room ID:', roomId);
    console.log('Schwellenwert original:', schwellenwert);
    console.log('Schwellenwert processed:', schwellenwertValue);
    console.log('Activated at:', this.activatedAt);
    console.log('Deactivated at:', this.deactivatedAt);
    console.log('Form valid:', this.frmIndicator.valid);
    console.log('Form errors:', this.frmIndicator.errors);
    
    // Log individual field errors
    Object.keys(this.frmIndicator.controls).forEach(key => {
      const control = this.frmIndicator.get(key);
      if (control && control.errors) {
        console.log(`Field ${key} errors:`, control.errors);
      }
    });
  }

  /**
   * Format date to German format (DD.MM.YYYY) - date only, no time
   */
  formatDateOnly(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  loadIndikatoren() {
    this.loading = true;
    this.indikatorenService.getIndikatoren(1, 100, '', 'status_id', 'desc').subscribe({
      next: (response) => {
        // Load all indikatoren (both active and inactive for editing)
        this.indikatoren = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading indikatoren:', error);
        this.loading = false;
      }
    });
  }

  /**
   * Format indikator number to 3 digits with leading zeros
   */
  formatIndikatorNumber(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(num) || num <= 0) {
      return '';
    }
    return num.toString().padStart(3, '0');
  }

  /**
   * Parse indikator number from string to integer, or return null if empty
   */
  parseIndikatorNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(num) || num <= 0) {
      return null;
    }
    return num;
  }

  async generateNewIndikatorNumber(): Promise<number> {
    try {
      // Get the highest indikator_number for this standort
      const response = await this.objektStrukturService.getMaxIndikatorNumber(this.standortId).toPromise();
      return (response?.maxNumber || 0) + 1;
    } catch (error) {
      console.error('Error generating indikator number:', error);
      // Fallback: return a timestamp-based number
      return Date.now() % 10000;
    }
  }

  onConfirm(): void {
    console.log('onConfirm called');
    console.log('Form valid:', this.frmIndicator.valid);
    console.log('Form value:', this.frmIndicator.value);
    
    // Log all field errors
    Object.keys(this.frmIndicator.controls).forEach(key => {
      const control = this.frmIndicator.get(key);
      if (control && control.errors) {
        console.log(`Field ${key} errors:`, control.errors);
        console.log(`Field ${key} value:`, control.value);
      }
    });
    
    if (this.frmIndicator.valid) {
      this.loading = true;
      const updateData = { ...this.frmIndicator.value };
      
      // Ensure status is a number (0 or 1)
      updateData.status = Number(updateData.status);
      
      // Format schwellenwert as integer (remove decimal places)
      if (updateData.schwellenwert !== null && updateData.schwellenwert !== undefined && updateData.schwellenwert !== '') {
        const numValue = parseFloat(updateData.schwellenwert);
        if (!isNaN(numValue)) {
          updateData.schwellenwert = Math.round(numValue).toString();
        }
      }
      
      // Parse indikator_number - convert to integer or null if empty (for auto-generation)
      const parsedNumber = this.parseIndikatorNumber(updateData.indikator_number);
      if (parsedNumber !== null) {
        updateData.indikator_number = parsedNumber;
      } else {
        // If empty, remove from updateData to allow backend auto-generation
        delete updateData.indikator_number;
      }
      
      // If status is being set to inactive (0), set updated_at
      if (updateData.status === 0) {
        updateData.updated_at = new Date().toISOString();
      }
      
      console.log('Updating room-indikator with data:', updateData);
      
      this.objektStrukturService.updateRoomIndikator(this.indicatorId, updateData)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.dialogRef.close({ 
              event: 'update', 
              data: {
                id: this.indicatorId,
                ...updateData
              }
            });
          },
          error: (error) => {
            this.loading = false;
            console.error('Error updating indicator:', error);
            // You could show an error message here
          }
        });
    } else {
      console.log('Form is not valid, cannot save');
    }
  }


  onDismiss(): void {
    this.dialogRef.close({ event: 'cancel' });
  }
}
